import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { PortfolioHealthCheckService } from '../services/portfolio-health-check/portfolio-health-check.service';
import { ChannelBrokerService } from '../services/channel-broker/channel-broker.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PortfolioHealthCheckGuard {

  constructor(
    private readonly channelBrokerService: ChannelBrokerService,
    private readonly router: Router,
    private readonly pw3ApiService: PortfolioHealthCheckService,
  ) { }

  canActivate(
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.channelBrokerService.isEligible$.pipe(
      take(1),
      switchMap(isEligible => {
        // If the user is not eligible redirect to portfolio watch application
        if (!isEligible) {
          window.location.href = environment.PORTFOLIO_WATCH;
        }

        // If the user is eligible check the portfolio data
        return this.channelBrokerService.userGoals$.pipe(
          take(1),
          map(data => {
            const userHasGoals = !!data?.diversifiedLevelText;

            if (userHasGoals) {
              // If user has goals redirect to phc page
              if (!this.isPHCLink(state.url) && !this.isRebalanceLink(state.url)) {
                this.router.navigate(['/']);
              } 
              return true;
            } else {
              // If not have goals redirect to no goals page
              if (!this.isPHCNoGoalsLink(state.url)) {
                this.router.navigate(['/no-goals']);
              }
              return true;
            }
          }),
          catchError(err => {
            console.error('error', err);
            this.pw3ApiService.setFailure(true);
            this.pw3ApiService.setLoading(false);
            return of(false);
          })
        );
      }),
      catchError(err => {
        console.error('error', err);
        window.location.href = environment.PORTFOLIO_WATCH;
        return of(false);
      })
    );
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Using the same logic as canActivate event
    return this.canActivate(route, state);
  }

  isPHCLink(url: string): boolean {
    return url === '/';
  }

  isPHCNoGoalsLink(url: string): boolean {
    return url === '/no-goals';
  }

  isRebalanceLink(url: string): boolean {
    return url === '/rebalancing-your-portfolio';
  }
}